import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription } from 'rxjs';
import { AxiosError } from 'axios';
import { Chart } from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { hu } from 'date-fns/locale';
import { differenceInMonths, format } from 'date-fns';
import {
  ChartParams,
  ChartResponse,
  ChartsService,
  CaseStartsChartData,
} from 'src/app/services/charts/charts.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeInfoComponent } from '../../../../../shared/payee-info/payee-info.component';
import { KeyValuePipe } from '@angular/common';

@Component({
  selector: 'app-case-starts-table',
  templateUrl: './case-starts-table.component.html',
  styleUrls: ['./case-starts-table.component.scss'],
  imports: [MatProgressSpinner, PayeeInfoComponent, KeyValuePipe],
})
export class CaseStartsTableComponent implements OnDestroy {
  private chartsService = inject(ChartsService);
  private snackbar = inject(MatSnackBar);

  private refreshSub?: Subscription;
  private casesStartedData?: ChartResponse;
  private casesStartedCanvas?: Chart<'bar', CaseStartsChartData, string>;
  private _casesStartedChart?: ElementRef<HTMLCanvasElement>;

  @Input() from_date?: Date;
  @Input() to_date?: Date;

  @Input({ required: true }) set refresh(refresh: Observable<void>) {
    this.refreshSub?.unsubscribe();

    this.refreshSub = refresh.subscribe(async () => await this.setData());
  }

  readonly loading = signal(false);
  @Output() loadingChange = new EventEmitter<boolean>();

  casesStartedDataSums: Record<string, number> = {};
  @ViewChild('casesStarted') set casesStartedChart(
    ref: undefined | ElementRef<HTMLCanvasElement>,
  ) {
    this._casesStartedChart = ref;
    if (ref) {
      this.setStartCaseChartData(ref.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
  }

  async setData(): Promise<void> {
    if (this.loading() || !this.from_date || !this.to_date) {
      return;
    }

    try {
      this.setLoading(true);
      this.casesStartedData = undefined;

      const params: ChartParams = {
        from_date: format(this.from_date, 'yyyy-MM-dd'),
        to_date: format(this.to_date, 'yyyy-MM-dd'),
      };

      this.casesStartedData = await this.chartsService.getChart(
        'case-starts',
        params,
      );

      this.setStartCaseSums();
      this.setStartCaseChartData(this._casesStartedChart?.nativeElement);
    } catch (error: AxiosError | unknown) {
      console.error('Error while getting partner table', error);
      let message = 'Hiba a partner táblázat betöltésekor';

      if (error instanceof AxiosError && error.response?.data?.message) {
        message = error.response.data.message;
      }

      this.snackbar.open(message, 'OK', { duration: 5000 });
    } finally {
      this.setLoading(false);
    }
  }

  private setStartCaseSums(): void {
    if (!this.casesStartedData) {
      return;
    }

    for (const dataset of this.casesStartedData.data.datasets) {
      this.casesStartedDataSums[dataset.label] = 0;
      for (const e of dataset.data) {
        this.casesStartedDataSums[dataset.label] += Number(e.y);
      }
    }
  }

  private setStartCaseChartData(chart?: HTMLCanvasElement): void {
    if (!chart || !this.casesStartedData || !this.from_date || !this.to_date) {
      return;
    }

    this.casesStartedCanvas?.destroy();

    let unit: 'day' | 'month' = 'day';
    if (differenceInMonths(this.to_date, this.from_date) >= 3) {
      unit = 'month';
    }

    this.casesStartedCanvas = new Chart<'bar', CaseStartsChartData, string>(
      chart,
      {
        data: this.casesStartedData.data,
        type: 'bar',
        options: {
          scales: {
            x: {
              type: 'time',
              min: this.from_date.getTime(),
              max: this.to_date.getTime(),
              adapters: {
                date: {
                  locale: hu,
                },
              },
              time: {
                displayFormats: {
                  day: 'yyyy.MM.dd.',
                  month: 'yyyy.MM.',
                },
                unit,
              },
            },
          },
        },
      },
    );
  }

  private setLoading(loading: boolean): void {
    this.loading.set(loading);
    this.loadingChange.emit(loading);
  }
}
